/*
 * Copyright (c) CIRROM 2023.
 */

import { API } from "./api";
import { DataLoaderAdditionalAttribute } from "./datasystems";

export type DataProjectType = "data_quality" | "monitor" | "sync" | "enrich";

export interface DataProject {
  name: string;
  id: number;
  system_type: string;
  integrated_system_id: number;
  enrich_system_id?: number | null;
  enrich_system_name?: string | null;
  score_start: number;
  score_latest: number;
  total_contacts: number;
  total_accounts: number;
  first_analysis: string;
  country_iso?: string;
  view_order: number;
  category: string;
  reloading: boolean;
  data_project_types?: string[];
  enrich_in_scope_total?: number;
  enrich_matched_total?: number;
  enrich_proposed_total?: number;
  enrich_field_changes?: number;
  enrich_matched_percentage?: number;
  enrich_fully_synced_percentage?: number;
}

export interface DataAlert {
  id?: number;
  description: string;
  recipients: string;
  frequency: string;
  alert_types: string[];
  source_type: string;
  source_type_id: number;
}

export interface DataProjectTicketCount {
  data_project_id: number;
  count: number;
}

export interface DataProjectListFilter {
  list_id: string;
  list_condition: string;
}

export interface DataProjectEntityFilter {
  attribute: string;
  value: string;
  condition: string;
}

export interface DataProjectFilterCondition {
  name: string;
  label: string;
}

export interface DataProjectFilterAttribute {
  attribute: string;
  label: string;
  conditions: DataProjectFilterCondition[];
}

export interface DataProjectFilterMetadata {
  list_filters: DataProjectFilterAttribute[];
  contact_filters: DataProjectFilterAttribute[];
  cirrom_attribute_filters: DataProjectFilterAttribute[];
  account_filters: DataProjectFilterAttribute[];
}

export interface DataProjectContact {
  id: number;
  crm_contact_id: string;
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  primary_email: string | null;
  secondary_email: string | null;
  company: string | null;
  job_title: string | null;
}

export interface DataAlertResult {
  id: number;
  batch_id: number;
  new_value: string;
  old_value: string;
  alert_type: string;
  record_id: number;
}

/**
 * Asynchronously retrieves all data projects for the user.
 *
 * @returns {Promise<DataProject[]>} A promise that resolves to an array of DataProject objects.
 */
export const getDataProjectsForUser = async (): Promise<DataProject[]> => {
  const response = await API.get("/get-all-data-projects");
  return response.data;
};

/**
 * Fetches data projects associated with a given integrated system.
 *
 * @param {string} integratedSystemId - The unique identifier for the integrated system.
 * @return {Promise<DataProject[]>} A promise that resolves to an array of DataProject objects.
 */
export const getDataProjectsForSystem = async (
  integratedSystemId
): Promise<DataProject[]> => {
  const response = await API.get("/get-all-data-projects", {
    params: { integrated_system_id: integratedSystemId },
  });
  return response.data;
};

/**
 * Asynchronously adds a new data project with the specified parameters by sending a PUT request to the server.
 *
 * @param {string} dataProjectName - The name of the data project to be added.
 * @param {any[]} list - A list of filters to be applied to the project.
 * @param {any[]} contactFilters - Filters related to contacts.
 * @param {any[]} cirromAttributeFilters - Filters related to CIRROM attributes.
 * @param {any[]} accountFilters - Filters related to accounts.
 * @param {number} integratedSystemId - The ID of the integrated system.
 * @param {DataProjectType} dataProjectType - The type of data project (optional, defaults to data_quality).
 * @returns {Promise<string>} A promise that resolves to the server's response data as a string.
 */
export const addDataProject = async (
  dataProjectName: string,
  list: DataProjectListFilter[],
  contactFilters: DataProjectEntityFilter[],
  cirromAttributeFilters: DataProjectEntityFilter[],
  accountFilters: DataProjectEntityFilter[],
  integratedSystemId: number,
  dataProjectType?: DataProjectType,
  syncSystemId?: number,
  accountAdditionalAttribute?: DataLoaderAdditionalAttribute,
  category?: string
): Promise<{ data_project_id: number; sync_group_id: number | null }> => {
  const response = await API.put("/add-data-project", {
    name: dataProjectName,
    integrated_system_id: integratedSystemId,
    sync_system_id: syncSystemId,
    account_additional_attribute: accountAdditionalAttribute?.id?.toString(),
    list_filters: list,
    contact_filters: contactFilters,
    cirrom_attribute_filters: cirromAttributeFilters,
    account_filters: accountFilters,
    data_project_type: dataProjectType || "data_quality",
    category,
  });
  return response.data;
};

export const getDataProjectFilterMetadata =
  async (): Promise<DataProjectFilterMetadata> => {
    const response = await API.get("/data-project-filter-metadata");
    return response.data;
  };

/**
 * Retrieves the ticket counts for a data project from an integrated system.
 *
 * @param {number} integratedSystemId - The unique identifier of the integrated system.
 * @param {number} offSet - The offset for pagination.
 * @returns {Promise<{res: DataProjectTicketCount[], next_off_set: number | null}>} - A promise that resolves to an object containing the ticket counts and the next offset for pagination.
 */
export const getDataProjectTicketCounts = async (
  integratedSystemId: number,
  offSet: number
): Promise<{ res: DataProjectTicketCount[]; next_off_set: number | null }> => {
  const response = await API.get("get-data-project-ticket-counts", {
    params: { integrated_system_id: integratedSystemId, off_set: offSet },
  });
  return response.data;
};

/**
 * Deletes a data project by its ID.
 *
 * This asynchronous function sends a DELETE request to the specified endpoint
 * to delete a data project. The deletion is soft by default, meaning the record
 * is not permanently removed from the database but flagged as deleted.
 *
 * @param {number} dataProjectId - The unique identifier of the data project to be deleted.
 * @return {Promise<any>} - A promise that resolves with the response data from the API call.
 */
export const deleteDataProject = async (
  dataProjectId: number
): Promise<any> => {
  const response = await API.delete("/delete-data-project", {
    params: { data_project_id: dataProjectId, hard_delete: false },
  });
  return response.data;
};

/**
 * Saves the specified order of data projects.
 *
 * @param {number[]} projectOrder - An array of project IDs representing the desired order.
 * @return {Promise<any>} A promise that resolves with the server's response data upon successful API call.
 */
export const saveViewOrder = async (projectOrder: number[]): Promise<any> => {
  const response = await API.put("/save-project-view-order", {
    view_order: projectOrder,
  });
  return response.data;
};

/**
 * Saves the project categories to the server.
 *
 * This function makes an asynchronous HTTP PUT request to the "/save-project-categories"
 * endpoint, sending the provided projects and categories data. Upon success, it returns
 * the response data from the server.
 *
 * @param {any[]} projectsAndCategories - An array containing project and category information to be saved.
 * @returns {Promise<any>} A promise that resolves to the server response data.
 */
export const saveDataProjectCategories = async (
  projectsAndCategories: any[]
): Promise<any> => {
  const response = await API.put("/save-project-categories", {
    data_projects: projectsAndCategories,
  });
  return response.data;
};

/**
 * Saves the project reload to the server.
 *
 * This function makes an asynchronous HTTP PUT request to the "/reload-data-project"
 * endpoint, sending the provided project id. Upon success, it returns
 * the response data from the server.
 *
 * @param {number} dataProjectId - The ID of the data project for which alerts are being fetched.
 * @returns {Promise<any>} A promise that resolves to the server response data.
 */
export const reloadDataProject = async (
  dataProjectId: number
): Promise<any> => {
  const response = await API.post("/reload-data-project", {
    data_project_id: dataProjectId,
  });
  return response.data;
};

/**
 * Fetches data alerts for a specified project.
 *
 * @param {number} dataProjectId - The ID of the data project for which alerts are being fetched.
 * @returns {Promise<DataAlert[]>} A promise that resolves to an array of DataProjectAlert objects.
 */
export const getDataAlertsForProject = async (
  dataProjectId: number
): Promise<DataAlert[]> => {
  const response = await API.get(`/data-project/${dataProjectId}/alerts`);
  return response.data;
};

/**
 * Fetches the contacts belonging to a specified data project.
 *
 * @param {number} dataProjectId - The ID of the data project for which contacts are being fetched.
 * @returns {Promise<DataProjectContact[]>} A promise that resolves to an array of DataProjectContact objects.
 */
export const getDataProjectContacts = async (
  dataProjectId: number
): Promise<DataProjectContact[]> => {
  const response = await API.get(`/data-project-contacts/${dataProjectId}`);
  return response.data;
};

/**
 * Asynchronously sends a data alert to the server and returns the response status and alert ID.
 *
 * @param {DataAlert} dataAlert - The data alert object to be sent to the server.
 * @returns {Promise<{status: string, id: number}>} - A promise that resolves to an object containing the status and alert ID.
 */
export const addDataAlert = async (
  dataAlert: DataAlert
): Promise<{ status: string; id: number }> => {
  const response = await API.post("/add-data-alert", dataAlert);
  return response.data;
};

/**
 * Asynchronously deletes a data alert with the given identifier.
 *
 * @param {number} dataAlertId - The unique identifier of the data alert to be deleted.
 * @returns {Promise<{status: string, message: string}>} A promise that resolves to an object containing the status and message of the deletion operation.
 */
export const deleteDataAlert = async (
  dataAlertId: number
): Promise<{ status: string; message: string }> => {
  const response = await API.delete("/data-alert", {
    params: { data_alert_id: dataAlertId },
  });
  return response.data;
};

/**
 * Asynchronously fetches metadata related to data alerts.
 *
 * @returns {Promise<{frequencies: string[], alert_types: string[]}>}
 * A promise that resolves to an object containing arrays of alert frequencies
 * and alert types.
 */
export const getDataAlertMetadata = async (): Promise<{
  frequencies: string[];
  alert_types: string[];
}> => {
  const response = await API.get("/data-alert-metadata");
  return response.data;
};

export const getDataAlertResults = async (
  dataAlertId
): Promise<DataAlertResult[]> => {
  const response = await API.get("/get-data-alert-results", {
    params: { data_alert_id: dataAlertId },
  });
  return response.data;
};
